import { AuthProvider } from '@40seas/entities/auth';

const AUTH_PROVIDER_OVERRIDE_KEY = 'auth_provider_override';
const AUTH_PROVIDER_POLICY_KEY = 'auth_provider_policy';

// Cache the provider determined at app startup to ensure consistency
let cachedProvider: AuthProvider | null = null;

/**
 * Get the current auth provider, checking in order:
 * 1. Reevol hostname → always self-managed
 * 2. URL parameter `?auth=` (highest priority, persists to localStorage)
 * 3. Server policy from localStorage (auth_provider_policy) — set by backend
 * 4. localStorage override (persists across page refreshes)
 * 5. Environment variable AUTH_PROVIDER
 * 6. Default to Auth0
 *
 * IMPORTANT: The provider is cached after first call to prevent mismatch
 * between AuthenticationProvider (which mounts once) and useAuth hook.
 * If URL parameter changes after mount, a page reload is triggered.
 */
export function getAuthProvider(): AuthProvider {
    // Parse URL once for both cached check and first-time evaluation
    const urlParams = new URLSearchParams(window.location.search);
    const urlOverride = urlParams.get('auth');
    const isValidOverride = urlOverride === AuthProvider.SelfManaged || urlOverride === AuthProvider.Auth0;
    const storedOverride = localStorage.getItem(AUTH_PROVIDER_OVERRIDE_KEY);
    const hostname = window.location.hostname.toLowerCase();
    const isReevolHost = hostname === 'reevol.com' || hostname.endsWith('.reevol.com');

    // Reevol domains must always use hosted (self-managed) auth.
    if (isReevolHost) {
        cachedProvider = AuthProvider.SelfManaged;
        if (storedOverride !== AuthProvider.SelfManaged) {
            localStorage.setItem(AUTH_PROVIDER_OVERRIDE_KEY, AuthProvider.SelfManaged);
        }
        return cachedProvider;
    }

    // Return cached value (but handle mid-session URL override first)
    if (cachedProvider !== null) {
        if (isValidOverride && urlOverride !== cachedProvider) {
            localStorage.setItem(AUTH_PROVIDER_OVERRIDE_KEY, urlOverride);
            window.location.reload();
        }
        return cachedProvider;
    }

    // First call - determine and cache provider

    // 1. URL parameter override (dev/testing)
    if (isValidOverride) {
        localStorage.setItem(AUTH_PROVIDER_OVERRIDE_KEY, urlOverride);
        cachedProvider = urlOverride as AuthProvider;
        return cachedProvider;
    }

    // 2. Server-driven policy (authoritative source set by backend)
    const serverPolicy = localStorage.getItem(AUTH_PROVIDER_POLICY_KEY);
    if (serverPolicy === AuthProvider.SelfManaged || serverPolicy === AuthProvider.Auth0) {
        cachedProvider = serverPolicy as AuthProvider;
        return cachedProvider;
    }

    // 3. Check localStorage override (persists URL override)
    const stored = localStorage.getItem(AUTH_PROVIDER_OVERRIDE_KEY);
    if (stored === AuthProvider.SelfManaged || stored === AuthProvider.Auth0) {
        cachedProvider = stored as AuthProvider;
        return cachedProvider;
    }

    // 4. Fall back to environment variable (validate against enum values)
    const envProvider = process.env.AUTH_PROVIDER;
    if (envProvider === AuthProvider.SelfManaged || envProvider === AuthProvider.Auth0) {
        cachedProvider = envProvider as AuthProvider;
        return cachedProvider;
    }

    // Default to self-managed if environment variable is invalid or not set
    cachedProvider = AuthProvider.SelfManaged;
    return cachedProvider;
}

/**
 * Store the server-driven auth provider policy in localStorage.
 * The stored value will be used on the next login to route the user
 * to the correct auth provider.
 */
export function setServerAuthPolicy(provider: AuthProvider): void {
    if (provider !== AuthProvider.SelfManaged && provider !== AuthProvider.Auth0) {
        return;
    }
    localStorage.setItem(AUTH_PROVIDER_POLICY_KEY, provider);
}

const AUTH_PROVIDER_TRANSITION_KEY = 'auth_provider_transition_until';

/**
 * Set a deadline (Unix timestamp in seconds) after which the Auth0 session
 * should not be refreshed. Used during auth0 → self-managed migration:
 * the user keeps their current session until the access token expires,
 * then they're redirected to login with the new provider.
 */
export function setAuthTransitionDeadline(tokenExpSeconds: number): void {
    localStorage.setItem(AUTH_PROVIDER_TRANSITION_KEY, tokenExpSeconds.toString());
}

/**
 * Returns true if a transition deadline exists and has passed.
 */
export function isAuthTransitionExpired(): boolean {
    const deadline = localStorage.getItem(AUTH_PROVIDER_TRANSITION_KEY);
    if (!deadline) return false;
    return Date.now() / 1000 >= parseInt(deadline);
}

/**
 * Clear the transition deadline (e.g. after the user has been redirected to login).
 */
export function clearAuthTransition(): void {
    localStorage.removeItem(AUTH_PROVIDER_TRANSITION_KEY);
}

/**
 * Clear the auth provider override from localStorage.
 * Useful for resetting to default behavior.
 * Note: Requires page reload to take effect.
 */
export function clearAuthProviderOverride(): void {
    localStorage.removeItem(AUTH_PROVIDER_OVERRIDE_KEY);
}

/**
 * Reset the cached provider. Only used for testing.
 * @internal
 */
export function resetCachedProvider(): void {
    cachedProvider = null;
}
